import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import StatCard from "@/components/StatCard";
import { Boxes, Building2, Users, Wrench, ShieldCheck, AlertTriangle, Activity, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const PIE_COLORS = ["#E63946", "#FCA311", "#10B981", "#3B82F6"];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard").then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#475569] text-sm" data-testid="dashboard-loading">Loading dashboard…</div>;
  if (!data) return null;
  const t = data.totals || {};

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">{user?.role} Dashboard</div>
          <h1 className="text-3xl lg:text-4xl tracking-tighter font-medium mt-1">Hello, {user?.name?.split(" ")[0]}</h1>
          <p className="text-[#475569] mt-1 text-sm">Real-time view of your supply chain & warranty operations.</p>
        </div>
        <div className="text-xs text-[#64748B]">{new Date().toLocaleString("en-IN")}</div>
      </div>

      {user?.role === "manufacturer" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <StatCard testId="stat-distributors" label="Distributors" value={t.distributors} icon={Building2} accent />
            <StatCard testId="stat-pending" label="Pending Approvals" value={t.pending_distributors} icon={AlertTriangle} hint={t.pending_distributors > 0 ? "Review →" : null} />
            <StatCard testId="stat-dealers" label="Dealers" value={t.dealers} icon={Users} />
            <StatCard testId="stat-installations" label="Installations" value={t.installations} icon={Wrench} />
            <StatCard testId="stat-total-stock" label="Total Stock" value={t.total_stock} icon={Boxes} />
            <StatCard testId="stat-avail-stock" label="At Manufacturer" value={t.available_stock} icon={Boxes} />
            <StatCard testId="stat-alloc-stock" label="Allocated" value={t.allocated_stock} icon={Boxes} />
            <StatCard testId="stat-active-warranties" label="Active Warranties" value={t.active_warranties} icon={ShieldCheck} accent />
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Stock Distribution</div>
                  <div className="text-lg font-medium">Lifecycle State</div>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={[
                    { name: "At Mfr.", value: t.available_stock || 0 },
                    { name: "Allocated", value: t.allocated_stock || 0 },
                    { name: "Consumed", value: t.consumed_stock || 0 },
                  ]}>
                    <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" stroke="#475569" fontSize={12} />
                    <YAxis stroke="#475569" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 8 }} />
                    <Bar dataKey="value" fill="#E63946" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Warranties</div>
              <div className="text-lg font-medium">Active vs Consumed Stock</div>
              <div className="h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Active Warranties", value: t.active_warranties || 0 },
                        { name: "Consumed Stock", value: t.consumed_stock || 0 },
                        { name: "Allocated", value: t.allocated_stock || 0 },
                      ]}
                      cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}
                      dataKey="value"
                    >
                      {PIE_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#F8FAFC", border: "1px solid #E5E7EB", borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: "#475569" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <SectionPanel title="Recent Installations" icon={Wrench}>
              {(data.recent_installations || []).length === 0 ? (
                <Empty label="No installations yet." />
              ) : (
                <div className="space-y-2">
                  {data.recent_installations.map((i) => (
                    <div key={i.id} className="flex items-center justify-between px-3 py-2.5 rounded-md bg-[#FFFFFF] border border-[#E5E7EB]">
                      <div>
                        <div className="text-sm">{i.customer_name} • <span className="font-mono text-[#E63946]">{i.vehicle_reg_no}</span></div>
                        <div className="text-[11px] text-[#64748B] mt-0.5">{i.product_name} · {i.ppf_code}</div>
                      </div>
                      <div className="text-[10px] text-[#64748B]">{fmt(i.installed_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </SectionPanel>

            <SectionPanel title="Recent Activity" icon={Activity}>
              {(data.recent_activity || []).length === 0 ? (
                <Empty label="No activity yet." />
              ) : (
                <div className="space-y-2">
                  {data.recent_activity.map((a) => (
                    <div key={a.id} className="flex items-center justify-between px-3 py-2 rounded-md bg-[#FFFFFF] border border-[#E5E7EB]">
                      <div>
                        <div className="text-xs font-mono text-[#E63946]">{a.action}</div>
                        <div className="text-[11px] text-[#64748B]">{a.entity_type} · {a.actor_role || "system"}</div>
                      </div>
                      <div className="text-[10px] text-[#64748B] inline-flex items-center gap-1">
                        <Clock size={10} /> {fmt(a.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionPanel>
          </div>
        </>
      )}

      {user?.role === "distributor" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard testId="stat-dealers" label="My Dealers" value={t.dealers} icon={Users} accent />
          <StatCard testId="stat-stock" label="Available Stock" value={t.available_stock} icon={Boxes} />
          <StatCard testId="stat-stock-dealers" label="At Dealers" value={t.stock_at_dealers} icon={Boxes} />
          <StatCard testId="stat-installations" label="Installations" value={t.installations} icon={Wrench} />
          <StatCard testId="stat-active-warranties" label="Active Warranties" value={t.active_warranties} icon={ShieldCheck} accent />
        </div>
      )}

      {user?.role === "dealer" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard testId="stat-avail" label="Available Stock" value={t.available_stock} icon={Boxes} accent />
          <StatCard testId="stat-consumed" label="Consumed" value={t.consumed_stock} icon={Boxes} />
          <StatCard testId="stat-installations" label="Installations" value={t.installations} icon={Wrench} />
          <StatCard testId="stat-active" label="Active Warranties" value={t.active_warranties} icon={ShieldCheck} accent />
        </div>
      )}
    </div>
  );
}

function SectionPanel({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-6">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={14} className="text-[#E63946]" />}
        <div className="text-sm font-medium">{title}</div>
      </div>
      {children}
    </div>
  );
}
function Empty({ label }) {
  return <div className="text-xs text-[#64748B] py-6 text-center">{label}</div>;
}
function fmt(d) { if (!d) return "-"; try { return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return d; } }
